import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { adminApi } from '@/lib/api';

const AdminFAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await adminApi.getFaqs();
      setFaqs(response.data);
    } catch (error) {
      toast.error('Failed to fetch FAQs');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditItem({
      question: '',
      answer: '',
      category: 'general',
      order: faqs.length,
      is_active: true
    });
    setEditModal(true);
  };

  const openEdit = (faq) => {
    setEditItem({ ...faq });
    setEditModal(true);
  };

  const handleSave = async () => {
    if (!editItem.question || !editItem.answer) {
      toast.error('Question and Answer are required');
      return;
    }

    setSaving(true);
    try {
      if (editItem.id) {
        await adminApi.updateFaq(editItem.id, editItem);
        setFaqs(faqs.map(f => f.id === editItem.id ? editItem : f));
        toast.success('FAQ updated');
      } else {
        const response = await adminApi.createFaq(editItem);
        setFaqs([...faqs, response.data]);
        toast.success('FAQ created');
      }
      setEditModal(false);
    } catch (error) {
      toast.error('Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      await adminApi.deleteFaq(id);
      setFaqs(faqs.filter(f => f.id !== id));
      toast.success('FAQ deleted');
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  const categories = ['general', 'services', 'pricing', 'process', 'support'];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-faq">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">FAQ Management</h1>
          <p className="text-muted-foreground">Manage frequently asked questions</p>
        </div>
        <Button onClick={openCreate} data-testid="add-faq-btn">
          <Plus className="w-4 h-4 mr-2" /> Add FAQ
        </Button>
      </div>

      <div className="space-y-4">
        {faqs.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <HelpCircle className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No FAQs yet. Add your first FAQ!</p>
            </CardContent>
          </Card>
        ) : (
          faqs.map((faq) => (
            <Card key={faq.id} className={!faq.is_active ? 'opacity-50' : ''}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{faq.question}</CardTitle>
                    <span className="inline-block px-2 py-1 text-xs bg-primary/10 text-primary rounded mt-2">
                      {faq.category}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(faq)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(faq.id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground whitespace-pre-wrap">{faq.answer}</p>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={editModal} onOpenChange={setEditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editItem?.id ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle>
            <DialogDescription>Fill in the FAQ details</DialogDescription>
          </DialogHeader>
          
          {editItem && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Question *</Label>
                <Input
                  value={editItem.question}
                  onChange={(e) => setEditItem({...editItem, question: e.target.value})}
                  placeholder="Enter the question"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Answer *</Label>
                <Textarea
                  value={editItem.answer}
                  onChange={(e) => setEditItem({...editItem, answer: e.target.value})}
                  rows={4}
                  placeholder="Enter the answer"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={editItem.category}
                    onValueChange={(v) => setEditItem({...editItem, category: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(c => (
                        <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Order</Label>
                  <Input
                    type="number"
                    value={editItem.order}
                    onChange={(e) => setEditItem({...editItem, order: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={editItem.is_active}
                  onCheckedChange={(c) => setEditItem({...editItem, is_active: c})}
                />
                <Label>Active</Label>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFAQ;
